/**
 * The per-SKU outcome bar — one shape carrying volume, outcome mix and
 * supply behaviour at once.
 *
 * ── What each channel of the shape means ──
 *
 *   LENGTH        = 流量. Scaled by SQRT of this SKU's requests against the
 *                   busiest SKU currently in the table.
 *   SEGMENTS      = outcome mix, as shares of THIS SKU's own requests, left
 *                   to right: 成功 (`ok`) → 不计入我方的拒绝 (`paper-mid`) →
 *                   我方失败 (`bad`). The middle segment is the WHITELISTED
 *                   subset (`refusalCodes.ts`), not the structural scalar:
 *                   `MODEL_NOT_AVAILABLE` never dialled an upstream either,
 *                   and it belongs in the red one because we could not serve
 *                   the request. When the code split is unavailable nothing
 *                   is deducted and the whole failure volume draws red.
 *   UNDER-TRACK   = supply misbehaviour (`serverErrors`), drawn beneath the
 *                   outcome bar rather than inside it.
 *
 * ── Why THOSE three segments ──
 *
 * They are a real partition of `requests`, which is the only reason they can
 * be drawn adjacent: `success = requests - errors`, and `types.ts` states
 * that `refusedBeforeUpstream` is a strict subset of `errors` so
 * `attributableErrors = errors - refusedBeforeUpstream` is sound. Adding a
 * 429 segment would NOT be sound — a 429 our own limiter issued never dialled
 * an upstream, so it can sit in both `rateLimited` and
 * `refusedBeforeUpstream`, and drawing both as adjacent slices would
 * double-count. The 429 count is text, in the row, instead.
 *
 * The middle segment is deliberately NEUTRAL grey rather than a failure
 * colour. Those requests are the admission gate, the balance gate and model
 * resolution refusing something on purpose — the system working. They are
 * excluded from the downstream verdict, and grey is what "excluded" looks
 * like next to a green and a red.
 *
 * ── Why the under-track is separate, and why it is the point ──
 *
 * `errors` and `serverErrors` are ORTHOGONAL populations, not nested
 * (`types.ts`): a request the retry ladder rescued ends 200 — it is NOT a
 * tenant error — but it does carry an upstream 5xx, so it IS a server error.
 * A SKU can therefore be all-green on top while the track underneath shows
 * supply wobbling and the ladder absorbing it. No number in a table cell
 * tells that story, and drawing the two on one stacked bar would imply a
 * containment that does not exist.
 *
 * The rescued portion (`fallbacks`) is drawn in `signal` at the HEAD of the
 * track, deliberately not in a failure colour: it is success the ladder had
 * to work for. Whatever 5xx exceeded it trails behind in `bad-bright`. The
 * track is as long as the LARGER of the two rates, so when the ladder
 * rescued everything the track is all head — which is exactly the reading
 * "supply wobbled, nobody downstream noticed".
 *
 * ── Why sqrt, and why that must be disclosed ──
 *
 * Production spans three orders of magnitude in a single window (9,427
 * requests on one SKU, single digits on another). Linear length would render
 * every small SKU as a one-pixel stub — the composition of the quiet rows,
 * which is often where a new problem shows first, would be unreadable. Sqrt
 * compresses that range while keeping the order intact: a 4× busier SKU is
 * 2× longer, not 4×.
 *
 * A non-linear length scale that is not disclosed is a lie about magnitude,
 * so `OutcomeLegend` states it in words on the page, and the exact request
 * count is always in the row as text and in the bar's `aria-label`. Length
 * ranks; the number quantifies.
 *
 * ── Accessibility (DESIGN.md §9) ──
 *
 * Every quantity the shape encodes also exists as text: requests, errors and
 * server errors have their own columns, the segment counts are printed under
 * the bar by `OutcomeBarCell`, and the `aria-label` restates all of them.
 * Colour and length are the second and third channels, never the only one.
 */

import { useT } from '../../i18n/index.ts';
import { formatInteger, formatMaybeInteger, formatRate } from './display.ts';
import type { ObservedTraffic } from './types.ts';

/** Bar length at the busiest SKU in the table, in SVG user units. */
const TRACK_WIDTH = 160;
const BAR_HEIGHT = 8;
const UNDER_Y = 10;
const UNDER_HEIGHT = 3;
const SVG_HEIGHT = 13;

/**
 * Floors, in user units. A SKU that served requests must never render as
 * nothing: "too small to see" and "no traffic" are different statements and
 * the bar has to keep them apart.
 */
const MIN_BAR = 8;
const MIN_SEGMENT = 1.5;
const MIN_UNDER = 2;

/**
 * Bar length for one SKU. Exported because the scale is a claim about
 * magnitude, and a claim about magnitude deserves its own test.
 */
export function outcomeBarLength(requests: number, maxRequests: number): number {
  if (requests <= 0 || maxRequests <= 0) return 0;
  return Math.max(MIN_BAR, TRACK_WIDTH * Math.sqrt(Math.min(1, requests / maxRequests)));
}

/** A visible width for a non-zero count, zero for a genuine zero. */
function segmentWidth(count: number, requests: number, length: number): number {
  if (count <= 0 || requests <= 0) return 0;
  return Math.max(MIN_SEGMENT, length * Math.min(1, count / requests));
}

interface Props {
  readonly traffic: ObservedTraffic;
  /** Requests of the busiest SKU currently in the table — the scale anchor. */
  readonly maxRequests: number;
  readonly testId: string;
}

/**
 * The bar alone. `OutcomeBarCell` is what a table row should use — it pairs
 * the shape with the text the shape is not allowed to replace.
 */
export function OutcomeBar({ traffic, maxRequests, testId }: Props): JSX.Element {
  const t = useT();
  const { requests, errors, serverErrors, fallbacks } = traffic;
  const length = outcomeBarLength(requests, maxRequests);
  const success = Math.max(0, requests - errors);
  // The EXCLUDED refusals, not the structural scalar: `MODEL_NOT_AVAILABLE`
  // never dialled an upstream either, and it is ours. Null when the split
  // cannot be computed at all, in which case nothing is deducted and the
  // whole failure volume is drawn as ours — the conservative direction.
  const excluded = traffic.expectedRefusals;

  // The LABEL says what the cell says, and the cell says "—" here. The
  // geometry below coerces a missing split to zero deducted, painting the
  // whole failure volume as ours — a drawing decision in the conservative
  // direction. The label does NOT inherit that coercion: a screen reader
  // hearing "7,044 failed on us" off a cell rendering an em dash would be
  // told a number the sighted surface deliberately refuses to state. Shape
  // may round; a spoken figure may not.
  const label = t('health_perspective.bar.aria', {
    requests: formatInteger(requests),
    success: formatInteger(success),
    refused: formatMaybeInteger(excluded),
    failed: formatMaybeInteger(traffic.attributableErrors),
    serverErrors: formatInteger(serverErrors),
    fallbacks: formatInteger(fallbacks),
  });

  if (requests <= 0) {
    // Idle is not "zero-length success"; it is nothing to judge. A hairline
    // stub in the disabled tone says so without pretending to a measurement.
    return (
      <svg
        role="img"
        aria-label={t('health_perspective.grade.idle')}
        width={TRACK_WIDTH}
        height={SVG_HEIGHT}
        data-testid={testId}
      >
        <rect x={0} y={0} width={MIN_BAR} height={BAR_HEIGHT} rx={1} className="fill-paper-faint" />
      </svg>
    );
  }

  // Widths are laid out cumulatively so the floors above cannot push the
  // last segment past the end of the bar.
  const successWidth = segmentWidth(success, requests, length);
  const refusedWidth = Math.min(
    segmentWidth(excluded ?? 0, requests, length),
    Math.max(0, length - successWidth),
  );
  const failedWidth = Math.max(0, length - successWidth - refusedWidth);

  const supplyRate = Math.min(1, Math.max(traffic.serverErrorRate, traffic.fallbackRate));
  const underWidth = supplyRate > 0 ? Math.max(MIN_UNDER, length * supplyRate) : 0;
  const rescuedWidth =
    fallbacks > 0 ? Math.min(underWidth, Math.max(MIN_SEGMENT, length * Math.min(1, traffic.fallbackRate))) : 0;

  return (
    <svg role="img" aria-label={label} width={TRACK_WIDTH} height={SVG_HEIGHT} data-testid={testId}>
      <rect x={0} y={0} width={successWidth} height={BAR_HEIGHT} rx={1} className="fill-ok" />
      <rect
        x={successWidth}
        y={0}
        width={refusedWidth}
        height={BAR_HEIGHT}
        className="fill-paper-mid"
        data-testid={`${testId}-refused`}
      />
      <rect
        x={successWidth + refusedWidth}
        y={0}
        width={failedWidth}
        height={BAR_HEIGHT}
        rx={1}
        className="fill-bad"
      />
      {underWidth > 0 ? (
        <g data-testid={`${testId}-supply-track`}>
          <rect
            x={0}
            y={UNDER_Y}
            width={underWidth}
            height={UNDER_HEIGHT}
            rx={1}
            className="fill-bad-bright"
          />
          {rescuedWidth > 0 ? (
            <rect
              x={0}
              y={UNDER_Y}
              width={rescuedWidth}
              height={UNDER_HEIGHT}
              rx={1}
              className="fill-signal"
              data-testid={`${testId}-rescued`}
            />
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}

/**
 * The bar plus the counts it encodes, as one table cell.
 *
 * The text line is not decoration and not a fallback: it is the primary
 * carrier of the three segment counts, which no column of this table prints.
 * The shape ranks and groups; the line states.
 */
export function OutcomeBarCell({ traffic, maxRequests, testId }: Props): JSX.Element {
  const t = useT();
  const success = Math.max(0, traffic.requests - traffic.errors);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <OutcomeBar traffic={traffic} maxRequests={maxRequests} testId={testId} />
      <span className="font-mono text-micro text-paper-low" data-testid={`${testId}-counts`}>
        {t('health_perspective.bar.counts', {
          success: formatInteger(success),
          // The deducted subset, and an em dash when it cannot be computed —
          // never a 0, which would read as "nothing was refused".
          refused: formatMaybeInteger(traffic.expectedRefusals),
          failed: formatMaybeInteger(traffic.attributableErrors),
        })}
      </span>
      {/* The tenant's own experience, which the three segments deliberately
          do NOT grade. It is a real number about a real customer, and a page
          that only reported the attributable rate would leave someone failing
          three quarters of their calls with nobody looking. */}
      <span className="font-mono text-micro text-paper-low" data-testid={`${testId}-client`}>
        {/* Tenant-final 429s beside the upstream 429s the ladder absorbed
            (#2240): the first is what the customer saw, the second is pool
            capacity the customer never saw. Unknown renders as unknown. */}
        {t('health_perspective.bar.client', {
          rate: formatRate(traffic.errorRate),
          rateLimited: formatInteger(traffic.rateLimited),
          upstreamRateLimited: formatMaybeInteger(traffic.upstreamAttemptRateLimited),
        })}
      </span>
    </div>
  );
}

/**
 * What the shapes mean, in words, on the page — including the sqrt scale.
 * Rendering the bars without this would be encoding magnitude in a channel
 * the reader has no way to decode.
 */
export function OutcomeLegend(): JSX.Element {
  const t = useT();
  return (
    <p
      className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-micro text-paper-low"
      data-testid="health-perspective-bar-legend"
    >
      <Swatch className="bg-ok" label={t('health_perspective.bar.legend.success')} />
      <Swatch className="bg-paper-mid" label={t('health_perspective.bar.legend.refused')} />
      <Swatch className="bg-bad" label={t('health_perspective.bar.legend.failed')} />
      <Swatch className="bg-bad-bright" label={t('health_perspective.bar.legend.supply')} />
      <Swatch className="bg-signal" label={t('health_perspective.bar.legend.rescued')} />
      <span>{t('health_perspective.bar.legend.scale')}</span>
    </p>
  );
}

function Swatch({ className, label }: { readonly className: string; readonly label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true" className={`inline-block h-2 w-3 rounded-sm ${className}`} />
      {label}
    </span>
  );
}
